import '@yanolja-org/core-ui/style.css'
import '@yanolja-org/search-header-widget/style.css'
import { CategoryDropdown, CoreUIDataProvider, Env, Header } from '@yanolja-org/core-ui'
import {
  createCategoryActionHandler,
  LogoOrBack,
  LogoOrClose,
  trackCategoryView,
} from '@yanolja-org/core-ui-adapter'
import { type ComponentProps, type MouseEvent as ReactMouseEvent, ReactNode, useRef } from 'react'
import { Footer } from '~/app/components-nds/common/footer'
import {
  CartButton,
  CloseButton,
  LogoButton,
  MyPageButton,
  RecentProductButton,
  WishButton,
} from '~/app/components-nds/common/header-page-layout/header-buttons'
import { PageName } from '@yanolja-org/search-header-widget/dist/proto-clientlogs/module/yanolja_frontend/page-name'
import { useNavigation } from '~/app/utils/use-navigation'
import conf from '~/app/conf'
import { ServiceEnv } from '@yanolja-org/search-header-widget/dist/config'
import { SearchWidget } from '@yanolja-org/search-header-widget'
import { getCartCount } from '~/app/react-query/common/cartCount'
import useIsomorphicLayoutEffect from '~/app/utils/useIsomorphicLayoutEffect'

/**
 * 모바일 헤더 좌측 버튼 타입
 *  - `back`: 웹뷰=뒤로가기, 브라우저=로고 (LogoOrBack — NolBridge 감지로 hydration 전 스왑)
 *  - `close`: 앱/웹 모두 닫기(X) — 풀모달 등 로고 fallback 이 없어야 하는 경우
 *  - `close-or-logo`: 웹뷰=닫기(X), 브라우저=로고 (LogoOrClose) — 종료성 '페이지'(예약완료 등)
 */
type MobileLeftSlotType = 'back' | 'close' | 'close-or-logo'

interface HeaderPageLayoutProps {
  children: ReactNode
  /** 모바일 > 왼쪽 버튼 타입 */
  mobileLeftSlotType: MobileLeftSlotType
  /** 모바일 > 오른쪽 영역 */
  mobileRightSlot?: ReactNode
  /** 모바일 > 타이틀 */
  mobileTitle?: string
  /** 모바일 > 타이틀 노출 애니메이션 */
  mobileTitleShowOnScroll?: boolean
  /** PC > 헤더 타입 */
  pcHeaderType: 'logo' | 'full'
  /**
   * PC full 헤더 - 현재 지면이 장바구니일 때 true.
   */
  isCartActive?: boolean
  /** Header.Bottom 에 추가되는 영역  */
  bottomSlot?: ReactNode
  /**
   * bottomSlot 을 콘텐츠폭(1320) 캡 없이 풀폭으로 노출한다 (배경 풀블리드).
   * Top 바(로고 등)는 그대로 콘텐츠폭 유지. 기본 false.
   */
  bottomSlotFullWidth?: boolean
  /** 푸터 노출 여부 */
  isShowFooter?: boolean
  /** 화면 하단 고정 CTA 여부 — footer가 가리지 않게 safe-area 를 확보한다 (Footer 로 전달) */
  hasBottomCta?: boolean
  /** 스크롤 시 헤더 하단 강조선 노출 여부. 자체 구분선 있는 지면(장바구니 등)은 false. */
  headerScrollEffect?: boolean
  /**
   * 모바일 > 왼쪽 **back/close 버튼** 클릭 시 추가 액션.
   * 이벤트를 받아 `e.preventDefault()` 하면 BackButton 의 기본 네비게이션(앱 dismiss / 뒤로가기)을 막을 수 있다.
   * (PreventBackDialog 등 호스트 자체 흐름을 보존해야 할 때 사용)
   *
   * NOTE: 이 핸들러와 `clickLogHandler.back`/`.close` 는 클릭 시 **둘 다** 호출된다.
   * 클릭 로그는 둘 중 **한 곳에만** 두어 중복 전송을 피한다.
   * NOTE: 모바일 Web 로고(`back`/`close-or-logo`) 클릭 시엔 호출되지 않는다.
   * 로고는 `clickLogHandler.logo` 만 사용한다(로고=홈 이동, 닫기와 별개). — 필요 시 `onClickMobileAction` 류로 이름 구체화 검토
   */
  onClickMobileLeftSlot?: (e: ReactMouseEvent<HTMLButtonElement>) => void
  /**
   * 헤더 wrapper 높이가 바뀔 때마다 측정값(px)을 전달한다. (내부에서 ResizeObserver 로 추적)
   * sticky 사이드바 top 계산 등 헤더 높이에 의존하는 레이아웃에서 사용.
   */
  onHeaderHeightChange?: (height: number) => void
  /** 검색창 로그 전송용 page name */
  pageName?: PageName
  /**
   * 클릭 로그 전송 핸들러 (로그 전용)
   *  - 공통헤더 로그 정의 완료 후 기타 아이콘 클릭 로그 전송 핸들러 추가 예정
   *  - `back`/`close` 는 `onClickMobileLeftSlot` 과 함께 호출되므로, 동일 로그를 양쪽에 두지 말 것 (중복 전송 방지)
   */
  clickLogHandler?: {
    back?: () => Promise<void> | void
    close?: () => Promise<void> | void
    logo?: () => Promise<void> | void
  }
}

/**
 * 헤더 좌측 슬롯.
 *  - `back`/`close-or-logo`: LogoOrBack/LogoOrClose 가 NolBridge 감지로 웹뷰=아이콘, 브라우저=로고를
 *    hydration 전에 스왑한다. 브라우저에선 로고가 모바일·PC 모두 노출되므로 PC 로고 역할까지 겸한다.
 *  - `close`: 풀모달 — 앱/웹 모두 닫기(X) 유지 (로고 fallback 없음).
 *  - 클릭은 자체 onClick 없이 Header 의 onAction(중앙 핸들러)으로 전달된다.
 */
const HeaderLeftSlot = ({ type }: { type: MobileLeftSlotType }) => {
  if (type === 'back') {
    return <LogoOrBack logoHref={conf.web.url} />
  }

  if (type === 'close-or-logo') {
    return <LogoOrClose logoHref={conf.web.url} />
  }

  return <CloseButton />
}

const PageHeader = ({
  mobileLeftSlotType,
  mobileRightSlot,
  mobileTitle,
  mobileTitleShowOnScroll,
  pcHeaderType,
  isCartActive,
  onClickMobileLeftSlot,
  bottomSlot,
  bottomSlotFullWidth,
  pageName,
  clickLogHandler,
  headerScrollEffect,
}: Omit<HeaderPageLayoutProps, 'children' | 'onHeaderHeightChange' | 'isShowFooter'>) => {
  const { onBackClick } = useNavigation()

  const env = conf.environment as Env

  const isPcFullType = pcHeaderType === 'full'

  /**
   * 자체 onClick 이 없는 좌측 슬롯(LogoOrBack/LogoOrClose/CloseButton) 클릭의 중앙 핸들러.
   * 자체 onClick 으로 동작이 끝나는 버튼(마이·찜·장바구니 등)의 action 은 처리하지 않는다.
   */
  const handleHeaderAction: NonNullable<ComponentProps<typeof Header>['onAction']> = (
    action,
    _meta,
    e
  ) => {
    switch (action) {
      // 웹뷰 뒤로가기 (LogoOrBack) — 호스트가 e.preventDefault() 하면 기본 네비게이션(앱 dismiss)을 막는다
      case 'back':
        onClickMobileLeftSlot?.(e as ReactMouseEvent<HTMLButtonElement>)
        clickLogHandler?.back?.()
        if (!e.defaultPrevented) {
          onBackClick()
        }
        break
      // 닫기 (CloseButton / LogoOrClose 웹뷰) — 닫기 동작 자체는 호스트의 onClickMobileLeftSlot 이 수행
      case 'close':
        onClickMobileLeftSlot?.(e as ReactMouseEvent<HTMLButtonElement>)
        clickLogHandler?.close?.()
        break
      // 로고 클릭 로그 — 이동은 LogoOrBack/LogoOrClose 의 logoHref(anchor) 또는 LogoButton 내부 핸들러가 수행
      case 'logo':
        clickLogHandler?.logo?.()
        break
      default:
        break
    }
  }

  return (
    <CoreUIDataProvider
      env={env}
      fetchers={{
        cart: async () => {
          const cartCount = await getCartCount()
          return { hasItem: !!cartCount.count }
        },
      }}
    >
      <CategoryDropdown
        contentClassName="w-full"
        onOpenChange={open => open && trackCategoryView()}
        onAction={createCategoryActionHandler({ env })}
      >
        <Header
          borderColor={'transparent'}
          scrollEffect={headerScrollEffect}
          onAction={handleHeaderAction}
        >
          <Header.Top>
            <Header.Left>
              {isPcFullType && <Header.CategoryTrigger showOnScroll={false} visibleOn="desktop" />}

              {/* 웹뷰: 뒤로가기/닫기, 브라우저: 로고 (모바일·PC 겸용) */}
              <HeaderLeftSlot type={mobileLeftSlotType} />

              {/* close(풀모달)는 좌측 슬롯에 로고가 없으므로 PC 로고를 별도 노출 */}
              {mobileLeftSlotType === 'close' && <LogoButton visibleOn="desktop" />}

              {mobileTitle && (
                <Header.Title
                  visibleOn="mobile"
                  showOnScroll={mobileTitleShowOnScroll}
                  className="typography-subtitle-16-bold"
                >
                  {mobileTitle}
                </Header.Title>
              )}

              {isPcFullType && (
                <Header.Content>
                  <SearchWidget
                    environment={conf.environment as ServiceEnv}
                    containerClassName="hidden pf-pc:block min-w-[400px]"
                    linkClassName="hidden"
                    logContext={{ sourcePageName: pageName || 'PAGE_NAME_UNSPECIFIED' }}
                  />
                </Header.Content>
              )}
            </Header.Left>

            {mobileRightSlot && <Header.Right visibleOn="mobile">{mobileRightSlot}</Header.Right>}

            {isPcFullType && (
              <Header.Right visibleOn="desktop">
                <MyPageButton />
                <WishButton />
                <CartButton active={isCartActive} />
                <RecentProductButton />
              </Header.Right>
            )}
          </Header.Top>
          <Header.Bottom
            behavior="fixed"
            // 풀블리드 시 core-ui escape-hatch 변수를 collapseRoot 에만 세팅해 1320 캡 해제 (CSS 변수는 아래로만 cascade 되므로 Top 바는 영향 없음)
            className={bottomSlotFullWidth ? '[--core-ui-header-content-width:100%]' : undefined}
            contentClassName={bottomSlotFullWidth ? 'px-0' : 'px-0 pf-pc:px-40'}
          >
            {bottomSlot}
          </Header.Bottom>
        </Header>
      </CategoryDropdown>
    </CoreUIDataProvider>
  )
}

/**
 * core-ui Header 를 사용하는 공통 페이지/풀모달 레이아웃.
 *
 * 디바이스 별 노출될 수 있는 케이스들은 다음과 같다.
 * 1. 모바일
 *  - 왼쪽: back / close / close-or-logo
 *    - `back`: 웹뷰=뒤로가기, 브라우저=로고 (LogoOrBack — NolBridge 감지, hydration 전 스왑)
 *    - `close`: 앱/웹 모두 닫기(X) — 풀모달 등
 *    - `close-or-logo`: 웹뷰=닫기(X), 브라우저=로고 (LogoOrClose) — 종료성 페이지(예약완료 등)
 *  - 오른쪽: custom 버튼 or 없음
 *
 * 2. PC
 *  - full type: 카테고리, 검색, 마이, 찜, 장바구니, 최근 본 모두 노출
 *  - only logo: 로고만 노출 - 주문서, 비회원 로그인, 휴대폰 인증 등 일부 페이지에서만 적용
 *  - 해당 없음 (PCDialogSheet으로 노출되기 때문에 헤더 영역 없음)
 */
const HeaderPageLayout = ({
  children,
  isShowFooter = true,
  hasBottomCta,
  onHeaderHeightChange,
  ...pageHeaderProps
}: HeaderPageLayoutProps) => {
  const headerRef = useRef<HTMLDivElement>(null)

  // 공통헤더(core-ui)는 fixed wrapper + spacer 높이를 비동기로 갱신하므로 1회 측정으로는 어긋난다.
  // 헤더 wrapper 높이를 ResizeObserver 로 추적해 변경 시마다 consumer 에 전달한다. (sticky top 계산 등)
  useIsomorphicLayoutEffect(() => {
    const headerEl = headerRef.current
    if (!headerEl || !onHeaderHeightChange) return

    const updateHeaderHeight = () => onHeaderHeightChange(headerEl.offsetHeight)
    updateHeaderHeight()

    const resizeObserver = new ResizeObserver(updateHeaderHeight)
    resizeObserver.observe(headerEl)
    return () => resizeObserver.disconnect()
  }, [onHeaderHeightChange])

  return (
    <div className="flex min-h-screen flex-col">
      <div ref={headerRef} className="sticky left-0 top-0 z-30">
        <PageHeader {...pageHeaderProps} />
      </div>
      <div className="flex flex-1 flex-col">{children}</div>
      {isShowFooter && <Footer hasBottomCta={hasBottomCta} />}
    </div>
  )
}

export default HeaderPageLayout
